/**
 * Notificaciones del navegador para las menciones (#277).
 *
 * **No es un canal nuevo.** El evento `notification:new` que hoy mueve el badge de la campanita es
 * el mismo que dispara esto: acá sólo se decide si además se muestra afuera del tab, y con qué
 * texto (`notification-copy.ts`, compartido con la bandeja).
 *
 * Alcance: **con el Admin abierto en alguna pestaña**, aunque no esté visible. Con el navegador
 * cerrado hace falta Web Push de verdad —Service Worker, VAPID, suscripción por dispositivo— y eso
 * es otro ticket.
 *
 * Los cuatro guards, y por qué cada uno:
 *
 * 1. **Preferencia + permiso.** Arranca apagado y no se pide nada al entrar: un permiso pedido de
 *    prendida es un permiso denegado, y denegado no se puede volver a pedir desde la página.
 * 2. **Foco, mirado entre pestañas.** Si el Operador está mirando el Admin, el badge alcanza. El
 *    foco se comparte por localStorage porque el caso real es tener dos pestañas: sin esto, la
 *    pestaña de atrás notifica lo que el Operador está leyendo adelante.
 * 3. **Una sola notificación por mención.** Con N pestañas abiertas el evento llega N veces. La
 *    primera que lo reclama en localStorage la muestra; las otras se callan.
 * 4. **Nada viejo, y nunca de a diez.** Al reconectar, el backlog se descarta por antigüedad; y
 *    como esa cuenta cruza el reloj del cliente con el del servidor, un tope de ráfaga la
 *    respalda: una máquina con la hora corrida no recibe el backlog entero ni queda muda.
 */
import { AppNotification } from "@/services/conversations.api";
import { mentionContext, mentionTitle } from "@/services/notification-copy";

const PREF_KEY = "xcons.notifications.browser";
const FOCUS_KEY = "xcons.notifications.focus";
const CLAIM_PREFIX = "xcons.notifications.claim.";

/** Cada cuánto la pestaña enfocada renueva su marca. */
const FOCUS_HEARTBEAT_MS = 1_000;
/** Marca de foco más vieja que esto = nadie está mirando (la pestaña se cerró sin avisar). */
const FOCUS_STALE_MS = 3_000;
/** Cuánto vive un reclamo antes de poder limpiarse. Sólo evita que localStorage crezca. */
const CLAIM_TTL_MS = 5 * 60_000;
/** Más viejo que esto no se notifica: es backlog, no una mención que está pasando ahora. */
const FRESH_WINDOW_MS = 60_000;
/** Tope duro de toasts seguidos, por si el reloj del cliente deja pasar un backlog entero. */
const BURST_MAX = 3;
const BURST_WINDOW_MS = 5_000;

/** Tag del toast: reemplaza en vez de apilar si el mismo evento llegara dos veces. */
const TAG_PREFIX = "xcons-mention-";

// ── soporte ────────────────────────────────────────────────────────────────

export function supportsNotifications(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export function currentPermission(): NotificationPermission {
  return supportsNotifications() ? Notification.permission : "denied";
}

// ── preferencia (persiste entre sesiones del mismo navegador) ──────────────

const prefListeners = new Set<() => void>();

function emitPref() {
  prefListeners.forEach((cb) => cb());
}

export function isEnabled(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) === "1";
  } catch {
    // Modo privado o storage bloqueado: sin dónde persistir, queda apagado.
    return false;
  }
}

export function setEnabled(value: boolean): void {
  try {
    localStorage.setItem(PREF_KEY, value ? "1" : "0");
  } catch {
    // Sin persistencia igual emitimos: la sesión actual respeta la decisión.
  }
  emitPref();
}

/** Suscribe a la preferencia. El `storage` nativo la sincroniza entre pestañas. */
export function subscribeEnabled(cb: () => void): () => void {
  prefListeners.add(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key === PREF_KEY || e.key === null) cb();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    prefListeners.delete(cb);
    window.removeEventListener("storage", onStorage);
  };
}

// ── permiso ────────────────────────────────────────────────────────────────

const permListeners = new Set<() => void>();

function emitPerm() {
  permListeners.forEach((cb) => cb());
}

/**
 * Suscribe al permiso del navegador.
 *
 * `Notification.permission` no es reactivo, así que además de lo que emitimos nosotros escuchamos
 * la Permissions API: si el Operador lo cambia desde el candado de la barra de direcciones, la UI
 * se entera sin recargar. No está en todos los navegadores y no pasa nada si falta.
 */
export function subscribePermission(cb: () => void): () => void {
  permListeners.add(cb);
  let status: PermissionStatus | null = null;
  // `query()` es asíncrono y el cleanup puede correr antes de que resuelva: ahí `status` todavía
  // es null y el listener quedaría enganchado para siempre. Pasa en cada unmount rápido y, bajo
  // StrictMode, en TODOS los montajes.
  let cancelled = false;
  const onChange = () => cb();
  // El try/catch abraza la llamada, no sólo la promesa: Safari tira `TypeError` **sincrónico**
  // para nombres que no soporta, y ahí un `.catch()` llega tarde. Sin esto, el throw sube por el
  // `subscribe` de useSyncExternalStore y se lleva puesta la pantalla.
  try {
    navigator.permissions
      ?.query({ name: "notifications" as PermissionName })
      .then((s) => {
        if (cancelled) return;
        status = s;
        s.addEventListener("change", onChange);
      })
      .catch(() => {});
  } catch {
    // Sin Permissions API el switch sigue andando: se entera del permiso al pedirlo.
  }
  return () => {
    cancelled = true;
    permListeners.delete(cb);
    status?.removeEventListener("change", onChange);
  };
}

/**
 * Pide el permiso y, si lo dan, deja la preferencia prendida.
 * Devuelve el permiso resultante para que la UI explique el `denied`.
 */
export async function requestPermission(): Promise<NotificationPermission> {
  if (!supportsNotifications()) return "denied";
  let result: NotificationPermission = Notification.permission;
  if (result === "default") {
    try {
      result = await Notification.requestPermission();
    } catch {
      result = "denied";
    }
  }
  if (result === "granted") setEnabled(true);
  emitPerm();
  return result;
}

// ── guard 2: foco compartido entre pestañas ────────────────────────────────

function thisTabFocused(): boolean {
  return document.visibilityState === "visible" && document.hasFocus();
}

function markFocus() {
  try {
    localStorage.setItem(FOCUS_KEY, String(Date.now()));
  } catch {
    // Sin storage no se comparte el foco; el guard local sigue valiendo.
  }
}

function someTabFocused(): boolean {
  // La propia pestaña primero: no depende de storage ni del heartbeat.
  if (thisTabFocused()) return true;
  try {
    const raw = localStorage.getItem(FOCUS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < FOCUS_STALE_MS;
  } catch {
    return false;
  }
}

/**
 * Arranca el heartbeat de foco. Lo llama el sider, que vive en toda pantalla.
 * Escribe sólo mientras esta pestaña está enfocada: la marca se vence sola al cambiar de ventana.
 */
export function startFocusHeartbeat(): () => void {
  const beat = () => {
    if (thisTabFocused()) markFocus();
  };
  beat();
  const timer = setInterval(beat, FOCUS_HEARTBEAT_MS);
  window.addEventListener("focus", beat);
  document.addEventListener("visibilitychange", beat);
  return () => {
    clearInterval(timer);
    window.removeEventListener("focus", beat);
    document.removeEventListener("visibilitychange", beat);
  };
}

// ── guard 3: una sola pestaña se queda con la mención ──────────────────────

function pruneClaims() {
  try {
    const cutoff = Date.now() - CLAIM_TTL_MS;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key?.startsWith(CLAIM_PREFIX)) continue;
      if (Number(localStorage.getItem(key)) < cutoff)
        localStorage.removeItem(key);
    }
  } catch {
    // Limpieza best-effort: si falla, los reclamos vencen igual por TTL en la próxima pasada.
  }
}

function claim(notificationId: string): boolean {
  try {
    const key = CLAIM_PREFIX + notificationId;
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, String(Date.now()));
    pruneClaims();
    return true;
  } catch {
    // Sin storage no hay forma de coordinar. Notificar de más sólo molesta con dos pestañas
    // abiertas; no notificar apaga la feature entera, que es peor.
    return true;
  }
}

// ── guard 4: nada viejo ────────────────────────────────────────────────────

function isFresh(n: AppNotification): boolean {
  const created = Date.parse(n.createdAt);
  // Fecha que no parsea: se trata como reciente. Perder una mención por un formato raro del
  // servidor es peor que mostrar una de más.
  if (Number.isNaN(created)) return true;
  const age = Date.now() - created;
  // Negativo = el reloj del cliente atrasa respecto del servidor. Es skew, no una mención del
  // futuro: recién llegó por el socket.
  if (age < 0) return true;
  return age < FRESH_WINDOW_MS;
}

/**
 * Tope de ráfaga, en memoria de esta pestaña.
 *
 * `isFresh` compara dos relojes distintos —el del cliente y el del servidor—, así que una máquina
 * desajustada puede dejar pasar un backlog entero. Esto lo corta igual: cualquiera sea el motivo,
 * nadie recibe diez toasts de golpe. Es el guard que no depende de la hora de nadie.
 */
let burst: number[] = [];

function withinBurstLimit(): boolean {
  const cutoff = Date.now() - BURST_WINDOW_MS;
  burst = burst.filter((t) => t > cutoff);
  if (burst.length >= BURST_MAX) return false;
  burst.push(Date.now());
  return true;
}

// ── el disparo ─────────────────────────────────────────────────────────────

/**
 * Muestra la mención afuera del tab, si los cuatro guards dejan.
 * `onOpen` enfoca la pestaña y navega al registro; lo provee quien escucha el socket.
 */
export function showMention(
  n: AppNotification,
  onOpen: (n: AppNotification) => void,
): void {
  if (!supportsNotifications()) return;
  if (!isEnabled() || Notification.permission !== "granted") return;
  if (someTabFocused()) return;
  if (!isFresh(n)) return;
  // El tope va ANTES del reclamo. Al revés, una mención cortada por la ráfaga queda igual marcada
  // en localStorage por todo el TTL: ninguna otra pestaña la toma y se pierde de verdad, no sólo
  // se pospone. El reclamo es estado compartido — no se gasta en algo que no se va a mostrar.
  if (!withinBurstLimit()) return;
  if (!claim(n.notificationId)) return;

  try {
    const notification = new Notification(mentionTitle(n), {
      body: `${mentionContext(n)}\n${n.preview}`,
      tag: TAG_PREFIX + n.notificationId,
      // El Operador decide cuándo atenderla: no se cierra sola a los pocos segundos.
      requireInteraction: true,
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
      onOpen(n);
    };
  } catch {
    // Algunos navegadores tiran si el constructor no está disponible (Android sin SW).
    // No hay nada que hacer: la mención sigue en el badge y en la bandeja.
  }
}
